import React, { useCallback, useMemo, useState } from "react";
import { getCountdownUrgency, useCountdown } from "@/hooks/useCountdown";
import { Clamp } from "@picklebet/ui";
import cx from "classnames";
import type { PickType } from "@/types/entryTypes";
import { StreamButton } from "@/features/shell/components/shared/StreamButton";
import { InfoPopover } from "@picklebet/ui";
import { formatUserDateTime, isValidDateGuard } from "@/utilities/datetime";
import { SELECTION_DATETIME } from "@/utilities/dateTimeFormats";
import type {
  ClampShape,
  ClampVariant,
} from "@/features/shell/components/shared/EventStatus";
import type {
  ProcessedPickemSelection,
  ProcessedSelection,
} from "@/hooks/firestore/betting/useEntries";
import { isPickemsSelection } from "@/features/entries/util/type-guards";

import * as styles from "./Countdown.module.scss";

const Countdown = ({
  scheduledStartTime,
  pick,
  selection,
  className,
  clampClassName,
  shape,
  variant,
}: {
  scheduledStartTime: Date | null;
  pick?: PickType;
  selection?: ProcessedSelection | ProcessedPickemSelection;
  className?: string;
  clampClassName?: string;
  shape?: ClampShape;
  variant?: ClampVariant;
}) => {
  const [remaining, secondsToNow] = useCountdown(scheduledStartTime);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isEminent, isSoon } = getCountdownUrgency(secondsToNow);

  const earlyBirdStream = secondsToNow <= 30 * 60; // 30 mins

  const popoverContent = useMemo(
    () => formatUserDateTime(scheduledStartTime, SELECTION_DATETIME),
    [scheduledStartTime],
  );

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen((open) => !open);
  }, []);
  const handleTriggerEnter = useCallback(() => setIsOpen(true), []);
  const handleTriggerLeave = useCallback(() => setIsOpen(false), []);

  const triggerProps = useMemo(
    () => ({
      onClick: handleTriggerClick,
      onMouseEnter: handleTriggerEnter,
      onMouseLeave: handleTriggerLeave,
    }),
    [handleTriggerClick, handleTriggerEnter, handleTriggerLeave],
  );

  if (!isValidDateGuard(scheduledStartTime)) return null;

  return (
    <div className={cx(styles.root, className)}>
      {earlyBirdStream && (pick || selection) && (
        <StreamButton
          title={pick?.eventName ?? selection?.event.eventName ?? ""}
          sport={pick?.sport ?? selection?.event.sport ?? "UNKNOWN"}
          url={
            pick?.streamUri ??
            (!isPickemsSelection(selection!)
              ? selection?.event.streamUri
              : "") ??
            ""
          }
          startTime={
            pick?.scheduledStartTime ??
            new Date(selection?.event.scheduledStartTime ?? "")
          }
          eventId={pick?.eventId ?? selection?.event.id ?? ""}
          isPlaceholder={
            pick?.isPlaceholder ??
            Boolean(selection?.event.attributes?.streamExpected) ??
            false
          }
        />
      )}

      <InfoPopover
        content={popoverContent}
        open={isOpen}
        onOpenChange={setIsOpen}
        triggerProps={triggerProps}
      >
        <Clamp
          className={cx(
            styles.countdown,
            {
              [styles.isSoon]: isSoon,
              [styles.isEminent]: isEminent,
            },
            clampClassName,
          )}
          shape={shape}
          variant={variant}
        >
          {remaining}
        </Clamp>
      </InfoPopover>
    </div>
  );
};

export { Countdown };
